/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * EditorPageCoverPicture components using @atlaskit/css
 * This file contains the new CSS implementation for the migration
 */
import { type FC, useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE as CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE_TYPE } from '../pageCoverPictureConstants.ts';

import type { CoverPictureWrapperProps } from './EditorPageCoverPictureWrapper.tsx';

const COVER_IMAGE_FIXED_HEIGHT = 147;
const SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH = 200;

const styles = cssMap({
	base: {
		position: 'relative',
		marginBottom: token('space.100'),
	},
	noMargin: {
		marginTop: token('space.0'),
		marginBottom: token('space.0'),
	},
	pointer: { cursor: 'pointer' },
	auto: { cursor: 'auto' },
	withPicture: { marginTop: token('space.075') },
	withoutPicture: { marginTop: token('space.0') },
	hidden: { height: '0px' },
	fixedWidth: { height: `${147 satisfies typeof COVER_IMAGE_FIXED_HEIGHT}px` },
	smartSpaceOverviewHeight: {
		height: `${200 satisfies typeof SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH}px`,
	},
	scaledWidth: {
		height:
			`round(down, calc(100cqw / (${38 / 7})), 1px)` as `${number}px` satisfies typeof CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE_TYPE,
	},
});

export const CoverPictureWrapperCss: FC<CoverPictureWrapperProps> = ({
	hasCoverPicture,
	isUploadingPicture,
	isReadOnly,
	isFixedWidthImageOption,
	isSmartSpaceOverview,
	width,
	children,
	...props
}) => {
	'use no memo'; // Exclude from React compiler - breaks unit tests when component uses compiled CSS

	// Dynamic width - must use style prop
	const computedWidth = useMemo(() => {
		if ((hasCoverPicture || isUploadingPicture) && isFixedWidthImageOption) {
			return '800px';
		}
		if ((hasCoverPicture || isUploadingPicture) && !isFixedWidthImageOption) {
			return '100%';
		}
		if ((hasCoverPicture || isUploadingPicture) && width) {
			return `${width}px`;
		}
		return '100%';
	}, [hasCoverPicture, isUploadingPicture, isFixedWidthImageOption, width]);

	const hasPicture = hasCoverPicture || isUploadingPicture;
	const showPictureMargin = !isSmartSpaceOverview && hasPicture;
	const showNoPictureMargin = !isSmartSpaceOverview && !hasPicture;

	return (
		<div
			{...props}
			style={{ width: computedWidth }}
			css={[
				styles.base,
				isSmartSpaceOverview && styles.noMargin,
				isReadOnly ? styles.auto : styles.pointer,
				showPictureMargin && styles.withPicture,
				showNoPictureMargin && styles.withoutPicture,
				!hasPicture
					? styles.hidden
					: isSmartSpaceOverview
						? styles.smartSpaceOverviewHeight
						: isFixedWidthImageOption
							? styles.fixedWidth
							: styles.scaledWidth,
			]}
		>
			{children}
		</div>
	);
};
